/* global React, Icon */
const { useState: useStateCta } = React;

function CtaBlock() {
  const [sent, setSent] = useStateCta(false);
  const [busy, setBusy] = useStateCta(false);
  const [err, setErr] = useStateCta("");
  const t = (k) => window.i18n.t(k);
  // Sale Desk bot — see src/data/botLinks.js (loaded earlier in main.entry.js).
  const links = window.HEALBOT_BOT_LINKS;
  const salesTgHref = links.tgLink(links.salesBot, "site_cta");

  const trackTgClick = () => {
    try {
      if (window.analytics && typeof window.analytics.track === "function") {
        window.analytics.track("sale_desk_cta_click", { source: "cta_block" });
      }
      if (window.__HB_DEBUG__) {
        // eslint-disable-next-line no-console
        console.debug("[cta] sale_desk_cta_click", { href: salesTgHref });
      }
    } catch (_e) {
      // Never let analytics break the navigation — fall through.
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy || sent) return;
    const f = e.target;
    const clinic = (f.cl.value || f.nm.value || "").trim();
    const email = (f.em.value || "").trim();
    // Optional: the form asks for a phone but a lead may skip it. Capped at 40
    // chars to match the server-side schema in services/bot/src/routes/cabinet.ts.
    const phone = (f.ph.value || "").trim().slice(0, 40) || undefined;
    const locale = (document.documentElement.lang || "uk").slice(0, 2);
    if (!clinic || !email) return;
    setBusy(true);
    setErr("");
    try {
      // Self-serve onboarding: creates the clinic (idempotent) and emails a
      // magic link so they can enter the cabinet and connect their bot.
      const res = await fetch("/api/cabinet/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: clinic, email, locale, phone }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSent(true);
    } catch {
      setErr(t("cta.form.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="section" id="cta">
      <div className="container">
        <div className="cta-wrap">
          <div className="cta-head">
            <span className="eyebrow">{t("cta.eyebrow")}</span>
            <h2 style={{ marginTop: 14 }}>{t("cta.h2")}</h2>
            <p>{t("cta.p")}</p>
          </div>
          <div className="cta-cols">
            <form className="cta-form" onSubmit={onSubmit}>
              <h3>{t("cta.form.h3")}</h3>
              <div className="form-row">
                <div className="field">
                  <label htmlFor="nm">{t("cta.form.name.label")}</label>
                  <input
                    id="nm"
                    type="text"
                    placeholder={t("cta.form.name.placeholder")}
                    required
                  />
                </div>
                <div className="field">
                  <label htmlFor="cl">{t("cta.form.clinic.label")}</label>
                  <input
                    id="cl"
                    type="text"
                    placeholder={t("cta.form.clinic.placeholder")}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="field">
                  <label htmlFor="em">{t("cta.form.email.label")}</label>
                  <input
                    id="em"
                    type="email"
                    placeholder={t("cta.form.email.placeholder")}
                    required
                  />
                </div>
                <div className="field">
                  <label htmlFor="ph">{t("cta.form.phone.label")}</label>
                  <input
                    id="ph"
                    type="text"
                    placeholder={t("cta.form.phone.placeholder")}
                    // Mirrors z.string().max(40) in services/bot/src/routes/cabinet.ts.
                    // The browser stops input at the limit, so the defensive
                    // slice() in onSubmit never has to silently truncate a
                    // number the lead believed they entered in full.
                    maxLength={40}
                    required
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-primary btn-lg" disabled={busy || sent}>
                {sent ? t("cta.form.sent") : busy ? "…" : t("cta.form.submit")}
                {!sent && !busy && <Icon name="arrow" size={16} />}
              </button>
              {sent && <p className="cta-magiclink">{t("cta.form.magiclink")}</p>}
              {err && (
                <p className="cta-error" role="alert">
                  {err}
                </p>
              )}
              <p className="consent">
                {t("cta.form.consent.pre")}
                <a href="#">{t("cta.form.consent.link")}</a>
                {t("cta.form.consent.post")}
              </p>
            </form>
            <aside className="cta-tg">
              <div className="tg-logo">
                <Icon name="tg" size={26} color="#fff" stroke={1.8} />
              </div>
              <h3>{t("cta.tg.h3")}</h3>
              <p>{t("cta.tg.p")}</p>
              <div className="cta-tg-card">
                {/* Card now presents the assistant, not the founder (cta.tg.name). */}
                <div className="ava">H</div>
                <div style={{ flex: 1 }}>
                  <div className="nm">{t("cta.tg.name")}</div>
                  <div className="st">{t("cta.tg.role")}</div>
                </div>
                <span
                  style={{ width: 8, height: 8, borderRadius: "50%", background: "#5EBA7D" }}
                ></span>
              </div>
              <div className="tg-action">
                <a href={salesTgHref} className="btn btn-tg btn-lg" onClick={trackTgClick}>
                  <Icon name="tg" size={18} /> {t("cta.tg.btn")}
                </a>
                <div style={{ fontSize: 12, color: "var(--muted)", textAlign: "center" }}>
                  {t("cta.tg.hours")}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
}

window.CtaBlock = CtaBlock;
